import {
    Autocomplete,
    AutocompleteProps,
    FormControl,
    FormHelperText,
    styled,
    TextField,
    useTheme,
} from "@mui/material";
import { DialogCloseIcon, SearchTextIcon } from "../../assets/SvgIcons";
import {
    useField,
    useFormikContext
} from "formik";
import { useFieldRequired } from "./FormSchemaContext";
import _ from "lodash"
import {
    useCallback,
    useEffect,
    useState
} from "react";

const BootstrapAutocomplete = styled(Autocomplete)(({ theme }) => ({
    "& .MuiOutlinedInput-root": {
        paddingBottom: "6px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
        borderRadius: "10px",
        backgroundColor: `${theme.palette.common.white}OD`,
        border: `0.5px solid ${theme.palette.text.primary}80`,
    },
    "& .MuiInputLabel-root": {
        ...theme.typography.body_500,
    },
    "& .MuiInputBase-input": {
        ...theme.typography.body_500,
    }
}));

interface FormikSearchDropdownProps
    extends Omit<AutocompleteProps<any, boolean, boolean, boolean>, "name" | "value" | "onChange" | "renderInput" | "options"> {
    name: string;
    options: {
        label: string;
        value: string;
        [key: string]: any
    }[];
    label: string;
    onChange?: (selectedObj: Record<string, any>) => void;
    fetchData?: (searchInput: string) => void;
    noOptionsText?: string;
    disabled?: boolean;
}

export default function FormikSearchDropdown({
    name,
    options,
    label,
    onChange,
    fetchData,
    noOptionsText = "No Search Results Found",
    disabled = false,
    ...props
}: FormikSearchDropdownProps): JSX.Element {
    const [isLoading, setLoading] = useState<boolean>(false)
    const [searchText, setSearchText] = useState<string>("")
    const [field, meta, helpers] = useField(name);
    const { setTouched } = helpers;

    const { setFieldValue } = useFormikContext();
    const theme = useTheme();

    const errorText = meta.error && meta.touched ? meta.error : "";
    const isRequired = useFieldRequired(name);

    const handleOnChange = (
        _: React.SyntheticEvent,
        value: unknown
    ) => {
        const selectedObj = value as { label: string; value: string; };

        if (onChange) {
            onChange(selectedObj)
        } else {
            setFieldValue(name, selectedObj)
        }
    };

    const debouncedFetchData = fetchData ? useCallback(
        _.debounce(async (searchQuery) => {
            try {
                if (searchQuery?.trim()?.length === 0) {
                    setLoading(false);
                    await fetchData('');
                    return;
                }
                setLoading(true);
                await fetchData(searchQuery);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        }, 500),
        [fetchData]
    ) : null

    useEffect(() => {
        if (debouncedFetchData) {
            debouncedFetchData(searchText);

            return () => {
                debouncedFetchData.cancel();
            };
        }
    }, [searchText]);

    return (
        <FormControl
            variant="outlined"
            fullWidth
            sx={{ my: { xs: 1, md: 2 } }}
        >
            <BootstrapAutocomplete
                options={options ?? []}
                loading={isLoading}
                popupIcon={<SearchTextIcon />}
                clearIcon={<DialogCloseIcon />}
                value={field.value ?? null}
                onChange={handleOnChange}
                sx={{
                    "& .MuiAutocomplete-popupIndicator": {
                        transform: "none"
                    },
                    "& .MuiAutocomplete-endAdornment": {
                        opacity: disabled ? 0.5 : 1
                    },
                    "& .MuiInputBase-root.Mui-disabled": {
                        cursor: "not-allowed",
                        "& .MuiInputBase-input": {
                            cursor: "not-allowed"
                        }
                    }
                }}
                componentsProps={{
                    paper: {
                        sx: {
                            backgroundColor: theme.palette.customColors.darkGrey,
                            color: theme.palette.text.primary,
                        },
                    },
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        onBlur={() => setTouched(true, true)}
                        placeholder="Search...."
                        inputProps={{
                            ...params.inputProps,
                            'aria-required': isRequired || undefined,
                        }}
                        sx={{
                            '& .MuiInputLabel-root': {
                                color: disabled ? `${theme.palette.text.secondary}B3` : theme.palette.customColors.infoCardTitle,
                            },
                        }}
                    />
                )}
                renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: any, state) => {
                    return (
                        <li {...props} key={`${option?.label ?? ""}-${option?.value ?? ""}-${state.index}`}>
                            {option?.label ?? ""}
                        </li>
                    );
                }}
                onInputChange={(event, value) => setSearchText(value)}
                noOptionsText={noOptionsText}
                disabled={disabled}
                isOptionEqualToValue={(option: unknown, value: unknown) => {
                    const optionValue = option as { value: string };
                    const valueValue = value as { value: string };
                    return optionValue.value === valueValue.value;
                }}
                {...props}
            />
            {errorText && (
                <FormHelperText
                    error
                    sx={{ marginLeft: 'unset', color: `${theme.palette.error[900]}`, fontWeight: 500 }}
                >
                    {errorText}
                </FormHelperText>
            )}
        </FormControl>
    );
}
